// Copyright (c) Ping. All rights reserved.
//
// Multi-account MLS group creation for web (and shared hosts).
// Phase 1: orchestration only — hosts
// supply `claimDevicesForAccount` (web: POST /v1/contacts/claim-fanout).

import { sha256 } from '@noble/hashes/sha256';

import type { ApiClient } from '../transport-mls/PingTransport';
import type {
  ConversationStore,
  MlsConversationLike,
  MlsKeyPackageEntry,
  MlsLogger,
  MessagingClientLike,
  KVStore,
  WelcomeRecipientSink,
} from './mlsConversation';
import {
  base64urlToBytes,
  bytesToBase64url,
  bytesToHex,
  canonicalDeviceIdHex,
  hexToBytes,
} from './mlsBytes';
import { registerCallGroup } from '../calls/callGroupRegistry';

// Max OTHER accounts in a group (self excluded). Raised from 7 → 256 to match
// the backend's per-request member cap (services/api/internal/conversations/
// validate.go `maxMembersPerRequest`) and to let group calls host 30+
// participants (a call rides a conversation, so the chat roster bounds the
// call roster). MLS TreeKEM rekey is ~O(log n); LiveKit hosts 30+ per room.
export const MAX_GROUP_PEER_ACCOUNTS = 256;

// Per-call MLS groups are named `call:<hex call_id>` so the client can tell a
// call group apart from a chat (the SDK ConversationMeta carries no `kind`).
// `useIncomingConversations` skips this prefix so call groups never surface as
// chat rows. calls.md §3.2.
export const CALL_GROUP_NAME_PREFIX = 'call:';
export const GROUP_CLAIM_CONCURRENCY = 4;
export const GROUP_CONV_META_PREFIX = 'ping:meta:v1:';
const MAX_GROUP_META_JSON_BYTES = 2048;
const DEFAULT_TITLE_MAX_LEN = 80;

const MLS_KEY_PREFIX = 'mls.conv.';
const INVITED_KEY_PREFIX = 'mls.invited.';

export interface GroupMemberInput {
  readonly accountId: string;
  readonly displayName: string;
}

export interface ClaimedDevice {
  readonly deviceIdHex: string;
  readonly keyPackageBytes: Uint8Array;
}

export type ClaimDevicesForAccount = (
  accountId: string,
  signal?: AbortSignal,
) => Promise<readonly ClaimedDevice[]>;

export interface CreateGroupConversationOptions {
  readonly client: MessagingClientLike;
  readonly transport: WelcomeRecipientSink | null;
  readonly api: ApiClient;
  readonly kv: KVStore;
  readonly conversations: ConversationStore;
  readonly log: MlsLogger;
  readonly localAccountId: string;
  readonly localDisplayName: string;
  /**
   * The creator's own device id (base64url, as the BE knows it). When present,
   * the group is REGISTERED server-side with `kind:'group_chat'` before the first
   * Welcome, so `messaging.conversations.kind` is authoritative and the server
   * never lazily defaults the group to `direct_message` (the root cause of a
   * single-peer group being mis-classified as a 1:1 on receivers). Best-effort —
   * omitted callers fall back to the lazy default + client-side reclassify.
   */
  readonly localDeviceId?: string;
  readonly members: readonly GroupMemberInput[];
  readonly displayName?: string;
  /**
   * Media id (base64url) of the group avatar, already uploaded to the media
   * server by the host. Embedded in the conversation meta (GroupContext name
   * extension) so every joining device gets the avatar from group state —
   * bulletproof, no separate hydration broadcast required. Optional: a group
   * created without an avatar omits it.
   */
  readonly avatarMediaId?: string;
  readonly claimDevicesForAccount: ClaimDevicesForAccount;
  /**
   * Pre-claimed devices of the LOCAL account's OTHER devices (linked siblings,
   * excluding this device). Invited in the SAME Commit as the peers so the new
   * conversation appears on every one of the creator's devices in realtime —
   * not only after a later link/admission. The host claims these (via
   * claim-fanout on its own account, minus its own device id) and passes them
   * in; `createGroupConversation` stays transport/identity-agnostic.
   */
  readonly selfSiblingDevices?: readonly ClaimedDevice[];
  /**
   * When true, reuse an existing group whose member set matches (the old
   * member-set dedup). Defaults to FALSE: an explicit "New Group" always mints a
   * fresh group, so multiple distinct groups with the same members are allowed
   * (and a single-peer group is never collapsed onto the 1:1). Set true only for
   * callers that intentionally want idempotent "open or create" semantics.
   */
  readonly reuseExisting?: boolean;
  readonly signal?: AbortSignal;
}

export interface CreateGroupConversationResult {
  readonly conversationIdHex: string;
  readonly conv: MlsConversationLike;
  readonly memberAccountIds: readonly string[];
  readonly displayName: string;
  readonly deduped: boolean;
  /** deviceIdHex → display name captured at invite time. */
  readonly memberDeviceNames: Readonly<Record<string, string>>;
  /** deviceIdHex → account id captured at invite time. */
  readonly memberDeviceAccounts: Readonly<Record<string, string>>;
}

export interface AddGroupMembersOptions {
  readonly client: MessagingClientLike;
  readonly transport: WelcomeRecipientSink | null;
  readonly conversationIdHex: string;
  readonly localAccountId: string;
  readonly localDisplayName: string;
  /** Current peer roster (sorted unique), excluding local user. */
  readonly existingPeerAccountIds: readonly string[];
  readonly newMembers: readonly GroupMemberInput[];
  readonly claimDevicesForAccount: ClaimDevicesForAccount;
  readonly log: MlsLogger;
  readonly signal?: AbortSignal;
}

export interface AddGroupMembersResult {
  readonly conv: MlsConversationLike;
  readonly addedPeerAccountIds: readonly string[];
  readonly memberAccountIds: readonly string[];
  readonly memberDeviceNames: Readonly<Record<string, string>>;
  readonly memberDeviceAccounts: Readonly<Record<string, string>>;
}

export interface RemoveGroupMembersOptions {
  readonly client: MessagingClientLike;
  readonly conversationIdHex: string;
  /** Accounts to evict (their device leaves are resolved via the MLS roster). */
  readonly removeAccountIds: readonly string[];
  readonly log: MlsLogger;
}

export interface RemoveGroupMembersResult {
  readonly conv: MlsConversationLike;
  /** Accounts whose leaves were actually removed (intersection of request ∩ roster). */
  readonly removedAccountIds: readonly string[];
  /** Number of MLS leaves (devices) evicted by the Commit. */
  readonly removedLeafCount: number;
}

export class GroupCreateError extends Error {
  readonly code: 'invalid_members' | 'peer_unavailable' | 'add_members_failed';
  readonly accountId: string | undefined;

  constructor(
    code: GroupCreateError['code'],
    message: string,
    accountId?: string | undefined,
  ) {
    super(message);
    this.name = 'GroupCreateError';
    this.code = code;
    this.accountId = accountId;
  }
}

export type GroupConvMeta = {
  readonly type: 'group';
  readonly inviter: { readonly accountId: string; readonly displayName: string };
  readonly members: readonly { readonly accountId: string; readonly displayName: string }[];
  readonly title?: string;
  /**
   * Media id (base64url) of the group avatar, resolvable by ANY device via
   * `GET /v1/media/{id}`. Carried in the conversation meta — which rides the
   * MLS GroupContext name extension — so a joining device gets the avatar from
   * group state (bulletproof), not only from a separate hydration broadcast.
   */
  readonly avatarMediaId?: string;
  /**
   * Explicit-group marker. `true` means "this is a deliberately-created group,
   * even if it has a single peer and no title/avatar". Set by every current
   * group producer. It exists to distinguish a real single-peer group from a
   * LEGACY 1:1 that historical builds stamped with `type:"group"` meta (those
   * lack this flag), so a `{me, B}` group is never mis-rendered as the 1:1 with
   * B — and vice-versa. See `parseGroupConvMeta`.
   */
  readonly sp?: boolean;
};

/** Normalize peer account IDs: unique, sorted, excluding the local user. */
export function normalizePeerAccountIds(
  localAccountId: string,
  members: readonly GroupMemberInput[],
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of members) {
    if (m.accountId === localAccountId) continue;
    if (seen.has(m.accountId)) continue;
    seen.add(m.accountId);
    out.push(m.accountId);
  }
  out.sort();
  return out;
}

export function memberAccountSetsEqual(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** Stable KV / dedup key — order of `peerAccountIds` must already be normalized. */
export function groupConversationCacheKey(
  localAccountId: string,
  peerAccountIds: readonly string[],
): string {
  const material = `${localAccountId}|${peerAccountIds.join(',')}`;
  const digest = sha256(new TextEncoder().encode(material));
  return `group:${bytesToHex(digest)}`;
}

export function defaultGroupDisplayName(members: readonly GroupMemberInput[]): string {
  const names = members.map((m) => m.displayName.trim()).filter((n) => n.length > 0);
  const joined = names.join(', ');
  if (joined.length <= DEFAULT_TITLE_MAX_LEN) return joined.length > 0 ? joined : 'Group chat';
  return `${joined.slice(0, DEFAULT_TITLE_MAX_LEN - 1)}…`;
}

/** Normalize a BE device id (hex or base64url) to canonical hex. */
function rosterDeviceKey(deviceIdHex: string): string {
  return canonicalDeviceIdHex(deviceIdHex) ?? deviceIdHex;
}

/** Map each claimed device id to the peer's display name at invite time. */
export function buildMemberDeviceNamesFromClaims(
  members: readonly GroupMemberInput[],
  peerAccountIds: readonly string[],
  claimedByAccount: readonly (readonly ClaimedDevice[])[],
): Record<string, string> {
  const nameByAccount = new Map(
    members.map((m) => [m.accountId, m.displayName.trim()] as const),
  );
  const out: Record<string, string> = {};
  for (let i = 0; i < peerAccountIds.length; i++) {
    const accountId = peerAccountIds[i]!;
    const displayName = nameByAccount.get(accountId) ?? accountId;
    for (const device of claimedByAccount[i] ?? []) {
      out[rosterDeviceKey(device.deviceIdHex)] = displayName;
    }
  }
  return out;
}

/** Map each claimed device id to the peer's account id at invite time. */
export function buildMemberDeviceAccountsFromClaims(
  peerAccountIds: readonly string[],
  claimedByAccount: readonly (readonly ClaimedDevice[])[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < peerAccountIds.length; i++) {
    const accountId = peerAccountIds[i]!;
    for (const device of claimedByAccount[i] ?? []) {
      out[rosterDeviceKey(device.deviceIdHex)] = accountId;
    }
  }
  return out;
}

export function buildGroupConversationMetaName(params: {
  readonly inviter: { readonly accountId: string; readonly displayName: string };
  readonly members: readonly GroupMemberInput[];
  readonly title?: string;
  readonly avatarMediaId?: string;
}): string {
  const meta: GroupConvMeta = {
    type: 'group',
    inviter: params.inviter,
    members: params.members.map((m) => ({
      accountId: m.accountId,
      displayName: m.displayName,
    })),
    // Mark this as an explicit group so a single-peer group with no title/avatar
    // is still classified as a group (never collapsed onto the 1:1) — see
    // `parseGroupConvMeta`.
    sp: true,
    ...(params.title !== undefined && params.title.length > 0
      ? { title: params.title }
      : {}),
    ...(params.avatarMediaId !== undefined && params.avatarMediaId.length > 0
      ? { avatarMediaId: params.avatarMediaId }
      : {}),
  };
  let json = JSON.stringify(meta);
  if (json.length > MAX_GROUP_META_JSON_BYTES) {
    // Drop members first (the long field); ALWAYS keep title + avatarMediaId —
    // they are tiny and are what a joiner needs to render the group correctly.
    const trimmed = {
      ...meta,
      members: meta.members.slice(0, Math.max(1, Math.floor(meta.members.length / 2))),
    };
    json = JSON.stringify(trimmed);
    if (json.length > MAX_GROUP_META_JSON_BYTES) {
      json = JSON.stringify({
        type: 'group',
        inviter: meta.inviter,
        members: [],
        sp: true,
        ...(meta.title !== undefined ? { title: meta.title } : {}),
        ...(meta.avatarMediaId !== undefined ? { avatarMediaId: meta.avatarMediaId } : {}),
      });
    }
  }
  return GROUP_CONV_META_PREFIX + json;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Parse MLS `createConversation({ name })` group metadata. */
export function parseGroupConvMeta(mlsConversationName: string): GroupConvMeta | null {
  if (!mlsConversationName.startsWith(GROUP_CONV_META_PREFIX)) {
    return null;
  }
  try {
    const raw: unknown = JSON.parse(
      mlsConversationName.slice(GROUP_CONV_META_PREFIX.length),
    );
    if (!isRecord(raw) || raw.type !== 'group') return null;
    if (!isRecord(raw.inviter)) return null;
    const inviterAccountId =
      typeof raw.inviter.accountId === 'string' ? raw.inviter.accountId : '';
    const inviterDisplayName =
      typeof raw.inviter.displayName === 'string' ? raw.inviter.displayName : '';
    if (inviterAccountId.length === 0) return null;
    const membersRaw = Array.isArray(raw.members) ? raw.members : [];
    const members: { accountId: string; displayName: string }[] = [];
    for (const entry of membersRaw) {
      if (!isRecord(entry)) continue;
      const accountId = typeof entry.accountId === 'string' ? entry.accountId : '';
      const displayName =
        typeof entry.displayName === 'string' ? entry.displayName : '';
      if (accountId.length === 0) continue;
      members.push({ accountId, displayName });
    }
    const title =
      typeof raw.title === 'string' && raw.title.trim().length > 0
        ? raw.title.trim()
        : undefined;
    const avatarMediaId =
      typeof raw.avatarMediaId === 'string' && raw.avatarMediaId.trim().length > 0
        ? raw.avatarMediaId.trim()
        : undefined;
    const explicitGroup = raw.sp === true;
    // Disambiguate a single-peer group from a LEGACY 1:1 stamped with
    // `type:"group"` meta. Historical iOS invite-accept builds stamped EVERY
    // conversation — including 1:1s — with group meta (the `members` array holds
    // only the peers, so a 1:1 has exactly one). A freshly-linked device
    // classifies purely from this MLS name, so without a guard every transferred
    // 1:1 would render as a group. A conversation is a REAL group when ANY of:
    //   * it has 2+ peers (a 1:1 never does),
    //   * it has an explicit title,
    //   * it carries a group `avatarMediaId` (a 1:1 never does),
    //   * it sets the explicit-group marker `sp` (every current group producer).
    // Only a single-peer, untitled, avatar-less, unmarked meta is treated as a
    // legacy 1:1 (return null) — so deliberate single-peer groups are respected.
    if (
      members.length <= 1 &&
      title === undefined &&
      avatarMediaId === undefined &&
      !explicitGroup
    ) {
      return null;
    }
    return {
      type: 'group',
      inviter: { accountId: inviterAccountId, displayName: inviterDisplayName },
      members,
      ...(explicitGroup ? { sp: true } : {}),
      ...(title !== undefined ? { title } : {}),
      ...(avatarMediaId !== undefined ? { avatarMediaId } : {}),
    };
  } catch {
    return null;
  }
}

export interface GroupConversationPatch {
  readonly kind: 'group';
  readonly name: string;
  /** The ADMIN-set title carried verbatim in the meta (`meta.title`), present
   *  ONLY when the group has an explicit title (not an auto-derived member-name
   *  list). This is the authoritative, cross-device source of truth for the
   *  group's name — rewritten into the GroupContext on every rename — so a
   *  reconcile may apply it OVER a stale name on any member, even one that
   *  missed the `com.ping.group_name` broadcast. `name` (which may be
   *  member-derived) must never overwrite a non-empty existing name. */
  readonly explicitTitle?: string;
  readonly memberAccountIds: string[];
  readonly memberDisplayNames: Record<string, string>;
  /** Media id of the group avatar carried in the meta, when present. The host
   *  resolves it to a download URL (`GET /v1/media/{id}`) and sets the row's
   *  avatar — propagated to a joining device via group state. */
  readonly avatarMediaId?: string;
  /** C3: the group OWNER (creator) account id, from the authenticated meta
   *  inviter. Immutable owner of the role hierarchy — used to bootstrap role
   *  authority on every member device without trusting any role broadcast. */
  readonly ownerAccountId?: string;
}

/** Build a local conversation row patch from embedded MLS group meta. */
export function groupConversationPatchFromMeta(
  mlsConversationName: string,
  localAccountId?: string | null,
): GroupConversationPatch | null {
  const meta = parseGroupConvMeta(mlsConversationName);
  if (meta === null) return null;

  const memberDisplayNames: Record<string, string> = {};
  const peerSet = new Set<string>();

  const addPeer = (accountId: string, displayName: string): void => {
    if (accountId.length === 0) return;
    if (localAccountId !== undefined && localAccountId !== null && accountId === localAccountId) {
      return;
    }
    peerSet.add(accountId);
    if (displayName.length > 0) {
      memberDisplayNames[accountId] = displayName;
    }
  };

  addPeer(meta.inviter.accountId, meta.inviter.displayName);
  for (const m of meta.members) {
    addPeer(m.accountId, m.displayName);
  }

  const peerAccountIds = [...peerSet].sort();
  const name =
    meta.title !== undefined && meta.title.length > 0
      ? meta.title
      : defaultGroupDisplayName(
          peerAccountIds.map((accountId) => ({
            accountId,
            displayName: memberDisplayNames[accountId] ?? accountId,
          })),
        );

  return {
    kind: 'group',
    name,
    ...(meta.title !== undefined && meta.title.length > 0 ? { explicitTitle: meta.title } : {}),
    memberAccountIds: peerAccountIds,
    memberDisplayNames,
    ...(meta.inviter.accountId.length > 0 ? { ownerAccountId: meta.inviter.accountId } : {}),
    ...(meta.avatarMediaId !== undefined ? { avatarMediaId: meta.avatarMediaId } : {}),
  };
}

export async function findExistingGroupByMembers(
  conversations: ConversationStore,
  _localAccountId: string,
  peerAccountIds: readonly string[],
): Promise<string | undefined> {
  const normalized = [...peerAccountIds].sort();
  if (conversations.findGroupByPeerAccountIds) {
    return conversations.findGroupByPeerAccountIds(normalized);
  }
  if (!conversations.listGroupConversations) {
    return undefined;
  }
  const rows = await conversations.listGroupConversations();
  for (const row of rows) {
    if (row.kind !== 'group' || !row.memberAccountIds) continue;
    const rowNormalized = [...row.memberAccountIds].sort();
    if (memberAccountSetsEqual(rowNormalized, normalized)) {
      return row.id;
    }
  }
  return undefined;
}

export async function createGroupConversation(
  opts: CreateGroupConversationOptions,
): Promise<CreateGroupConversationResult> {
  const {
    client,
    transport,
    api,
    kv,
    conversations,
    log,
    localAccountId,
    localDisplayName,
    localDeviceId,
    members,
    claimDevicesForAccount,
    signal,
  } = opts;

  const peerAccountIds = normalizePeerAccountIds(localAccountId, members);
  if (peerAccountIds.length < 1) {
    throw new GroupCreateError(
      'invalid_members',
      'Select at least one other person for a group chat.',
    );
  }
  if (peerAccountIds.length > MAX_GROUP_PEER_ACCOUNTS) {
    throw new GroupCreateError(
      'invalid_members',
      `Groups can have at most ${MAX_GROUP_PEER_ACCOUNTS} other members.`,
    );
  }

  const displayName =
    opts.displayName?.trim() ||
    defaultGroupDisplayName(members.filter((m) => m.accountId !== localAccountId));

  // Member-set dedup is OPT-IN now: an explicit New Group always creates a
  // fresh group (multiple groups with the same members are allowed, and a
  // single-peer group is never collapsed onto the 1:1). Callers that want
  // idempotent reuse pass `reuseExisting: true`.
  const existingId = opts.reuseExisting
    ? await findExistingGroupByMembers(conversations, localAccountId, peerAccountIds)
    : undefined;
  if (existingId) {
    log.info({
      event: 'groupConversation.dedup',
      conversationIdPrefix: existingId.slice(0, 8),
      peerCount: peerAccountIds.length,
    });
    const existingRow = await conversations.get(existingId);
    const conv = client.getConversation(hexToBytes(existingId));
    return {
      conversationIdHex: existingId,
      conv,
      memberAccountIds: peerAccountIds,
      displayName: existingRow?.name ?? displayName,
      deduped: true,
      memberDeviceNames: {},
      memberDeviceAccounts: {},
    };
  }

  const claimedByAccount = await claimAllPeerDevices(
    peerAccountIds,
    claimDevicesForAccount,
    signal,
  );

  const memberDeviceNames = buildMemberDeviceNamesFromClaims(
    members,
    peerAccountIds,
    claimedByAccount,
  );
  const memberDeviceAccounts = buildMemberDeviceAccountsFromClaims(
    peerAccountIds,
    claimedByAccount,
  );

  const peerDevices = claimedByAccount.flat();
  if (peerDevices.length === 0) {
    throw new GroupCreateError(
      'peer_unavailable',
      'No devices were available to invite.',
    );
  }
  // Invite the creator's OWN other devices in the SAME Commit so the group
  // appears on every linked device in realtime (web parity with iOS).
  const allDevices = [...peerDevices, ...(opts.selfSiblingDevices ?? [])];

  const trimmedTitle = opts.displayName?.trim();
  const metaName = buildGroupConversationMetaName({
    inviter: { accountId: localAccountId, displayName: localDisplayName },
    members,
    ...(trimmedTitle !== undefined && trimmedTitle.length > 0
      ? { title: trimmedTitle }
      : {}),
    ...(opts.avatarMediaId !== undefined && opts.avatarMediaId.length > 0
      ? { avatarMediaId: opts.avatarMediaId }
      : {}),
  });

  const conv = await client.createConversation({ name: metaName });
  const conversationIdHex = bytesToHex(conv.id);

  // SERVER-AUTHORITATIVE KIND: register the conversation as a GROUP before the
  // first Welcome, so message-service's lazy `ensure_conversation_row` (which
  // hard-defaults to `direct_message`) can't mis-record this group as a 1:1. This
  // is the root fix for a single-peer group {me, B} rendering as the 1:1 with B on
  // receivers. Best-effort + fire-before-invite: a 409 (already exists) or a
  // transient failure is non-fatal — the client-side reclassify is the fallback.
  if (localDeviceId !== undefined && localDeviceId.length > 0) {
    try {
      await api.post('/v1/conversations', {
        conversation_id: bytesToBase64url(conv.id),
        kind: 'group_chat',
        members: [{ device_id: localDeviceId, leaf_index: 0, joined_epoch: 0 }],
      });
      log.info({
        event: 'groupConversation.registerKind.ok',
        conversationIdPrefix: conversationIdHex.slice(0, 8),
      });
    } catch (err) {
      log.info({
        event: 'groupConversation.registerKind.skipped',
        conversationIdPrefix: conversationIdHex.slice(0, 8),
        message: err instanceof Error ? err.message : 'register failed',
      });
    }
  }

  try {
    await inviteDevicesToConversation({
      conv,
      transport,
      conversationIdHex,
      devices: allDevices,
      kv,
      localAccountId,
      peerAccountIds,
      log,
      skipKv: false,
    });
  } catch (err) {
    log.error({
      event: 'groupConversation.add_members_failed',
      conversationIdPrefix: conversationIdHex.slice(0, 8),
      deviceCount: allDevices.length,
    });
    throw new GroupCreateError(
      'add_members_failed',
      err instanceof Error ? err.message : 'Could not invite members to the group.',
    );
  }

  await kv.put({
    key: MLS_KEY_PREFIX + conversationIdHex,
    value: { bytes: Array.from(conv.id) },
    updatedAt: Date.now(),
  });

  log.info({
    event: 'groupConversation.created',
    conversationIdPrefix: conversationIdHex.slice(0, 8),
    peerCount: peerAccountIds.length,
    deviceCount: allDevices.length,
  });

  return {
    conversationIdHex,
    conv,
    memberAccountIds: peerAccountIds,
    displayName,
    deduped: false,
    memberDeviceNames,
    memberDeviceAccounts,
  };
}

export interface CreateCallGroupOptions {
  readonly client: MessagingClientLike;
  readonly transport: WelcomeRecipientSink | null;
  readonly kv: KVStore;
  readonly log: MlsLogger;
  readonly localAccountId: string;
  /** Other participants to add (self excluded). */
  readonly members: readonly GroupMemberInput[];
  /** Lowercase-hex call id; becomes the group name `call:<callId>`. */
  readonly callId: string;
  readonly claimDevicesForAccount: ClaimDevicesForAccount;
  readonly signal?: AbortSignal;
}

export interface CreateCallGroupResult {
  /** The per-call MLS group's conversation id (hex) = call_group_id. */
  readonly callGroupIdHex: string;
  readonly conv: MlsConversationLike;
  readonly memberAccountIds: readonly string[];
}

/**
 * Create a dedicated per-call MLS group (calls.md §3–4). Unlike
 * `createGroupConversation` this is ephemeral call infrastructure, not a chat:
 * it does NOT dedup, write chat meta, or send hydration, and it is named
 * `call:<callId>` so the client filters it out of the chat list. The media key
 * is later exported from THIS group, giving per-call PCS (only participants can
 * derive it; it rotates on every call join/leave Commit).
 *
 * The Welcome the SDK emits lazily registers the group + its members in the
 * backend's `messaging.conversation_members` (message-service append path), so
 * the token-mint membership check works with no extra API call.
 */
export async function createCallGroup(
  opts: CreateCallGroupOptions,
): Promise<CreateCallGroupResult> {
  const { client, transport, kv, log, localAccountId, members, callId, claimDevicesForAccount, signal } =
    opts;

  const peerAccountIds = normalizePeerAccountIds(localAccountId, members);
  if (peerAccountIds.length < 1) {
    throw new GroupCreateError('invalid_members', 'A call needs at least one other participant.');
  }
  if (peerAccountIds.length > MAX_GROUP_PEER_ACCOUNTS) {
    throw new GroupCreateError(
      'invalid_members',
      `A call can have at most ${MAX_GROUP_PEER_ACCOUNTS} other participants.`,
    );
  }

  const claimedByAccount = await claimAllPeerDevices(peerAccountIds, claimDevicesForAccount, signal);
  const allDevices = claimedByAccount.flat();
  if (allDevices.length === 0) {
    throw new GroupCreateError('peer_unavailable', 'No devices were available to ring.');
  }

  // One batched addMembers Commit for the whole roster (no join-storm).
  const conv = await client.createConversation({ name: `${CALL_GROUP_NAME_PREFIX}${callId}` });
  const callGroupIdHex = bytesToHex(conv.id);
  // Register immediately — before any further awaits — so that the SDK's
  // onConversationUpdated callback (which fires when createConversation
  // returns) cannot race with the caller's suppressCallGroup in callStore.
  // Without this, persistIfNew's async chain may complete and insert a chat
  // row (+ notify) before callStore ever calls suppressCallGroup.
  registerCallGroup(callGroupIdHex);

  try {
    await inviteDevicesToConversation({
      conv,
      transport,
      conversationIdHex: callGroupIdHex,
      devices: allDevices,
      kv,
      localAccountId,
      peerAccountIds,
      log,
      skipKv: false,
    });
  } catch (err) {
    throw new GroupCreateError(
      'add_members_failed',
      err instanceof Error ? err.message : 'Could not add participants to the call.',
    );
  }

  await kv.put({
    key: MLS_KEY_PREFIX + callGroupIdHex,
    value: { bytes: Array.from(conv.id) },
    updatedAt: Date.now(),
  });

  log.info({
    event: 'callGroup.created',
    callGroupIdPrefix: callGroupIdHex.slice(0, 8),
    deviceCount: allDevices.length,
  });

  return { callGroupIdHex, conv, memberAccountIds: peerAccountIds };
}

/**
 * Invite additional accounts into an existing MLS group (RFC 9420 Commit +
 * Welcome). Host merges `addedPeerAccountIds` into the local Dexie roster.
 */
export async function addMembersToGroupConversation(
  opts: AddGroupMembersOptions,
): Promise<AddGroupMembersResult> {
  const {
    client,
    transport,
    conversationIdHex,
    localAccountId,
    localDisplayName,
    existingPeerAccountIds,
    claimDevicesForAccount,
    log,
    signal,
  } = opts;

  const newPeerIds = normalizePeerAccountIds(localAccountId, opts.newMembers).filter(
    (id) => !existingPeerAccountIds.includes(id),
  );

  if (newPeerIds.length === 0) {
    throw new GroupCreateError(
      'invalid_members',
      'Everyone you selected is already in this group.',
    );
  }

  const mergedPeerIds = [...new Set([...existingPeerAccountIds, ...newPeerIds])].sort();
  if (mergedPeerIds.length > MAX_GROUP_PEER_ACCOUNTS) {
    throw new GroupCreateError(
      'invalid_members',
      `Groups can have at most ${MAX_GROUP_PEER_ACCOUNTS} other members.`,
    );
  }

  const claimedByAccount = await claimAllPeerDevices(
    newPeerIds,
    claimDevicesForAccount,
    signal,
  );
  const memberDeviceNames = buildMemberDeviceNamesFromClaims(
    opts.newMembers,
    newPeerIds,
    claimedByAccount,
  );
  const memberDeviceAccounts = buildMemberDeviceAccountsFromClaims(
    newPeerIds,
    claimedByAccount,
  );
  const allDevices = claimedByAccount.flat();
  if (allDevices.length === 0) {
    throw new GroupCreateError(
      'peer_unavailable',
      'No devices were available to invite.',
    );
  }

  const conv = client.getConversation(hexToBytes(conversationIdHex));

  try {
    await inviteDevicesToConversation({
      conv,
      transport,
      conversationIdHex,
      devices: allDevices,
      log,
      skipKv: true,
    });
  } catch (err) {
    log.error({
      event: 'groupConversation.add_members_existing_failed',
      conversationIdPrefix: conversationIdHex.slice(0, 8),
      deviceCount: allDevices.length,
    });
    throw new GroupCreateError(
      'add_members_failed',
      err instanceof Error ? err.message : 'Could not add members to the group.',
    );
  }

  log.info({
    event: 'groupConversation.members_added',
    conversationIdPrefix: conversationIdHex.slice(0, 8),
    addedCount: newPeerIds.length,
    deviceCount: allDevices.length,
    inviter: localDisplayName.slice(0, 1),
  });

  return {
    conv,
    addedPeerAccountIds: newPeerIds,
    memberAccountIds: mergedPeerIds,
    memberDeviceNames,
    memberDeviceAccounts,
  };
}

/**
 * Evict accounts from an existing MLS group (RFC 9420 Commit removing every
 * leaf that belongs to a target account). Mirrors {@link
 * addMembersToGroupConversation} for the reverse direction: it resolves the
 * SDK member roster (`client.members`), maps each `removeAccountId` →
 * leaf index(es) via `base64url(user_id) === accountId`, then issues a single
 * `conv.removeMembers(leaves)` Commit. The caller is responsible for patching
 * the local Dexie roster + announcing the change (e.g. a `com.ping.member`
 * leave event). Throws {@link GroupCreateError} when the binding can't manage
 * members or none of the targets are present.
 */
export async function removeMembersFromGroupConversation(
  opts: RemoveGroupMembersOptions,
): Promise<RemoveGroupMembersResult> {
  const { client, conversationIdHex, removeAccountIds, log } = opts;

  const conv = client.getConversation(hexToBytes(conversationIdHex));
  if (typeof client.members !== 'function' || typeof conv.removeMembers !== 'function') {
    throw new GroupCreateError(
      'add_members_failed',
      'This device can’t manage group members yet — update the app and try again.',
    );
  }

  const targets = new Set(removeAccountIds.filter((id) => id.length > 0));
  if (targets.size === 0) {
    throw new GroupCreateError('invalid_members', 'No members were selected to remove.');
  }

  const roster = await client.members(hexToBytes(conversationIdHex));
  const removedAccounts = new Set<string>();
  const leaves: number[] = [];
  for (const m of roster) {
    const acct = bytesToBase64url(m.user_id);
    if (targets.has(acct)) {
      leaves.push(m.leaf_index);
      removedAccounts.add(acct);
    }
  }

  if (leaves.length === 0) {
    throw new GroupCreateError(
      'invalid_members',
      'Those members are no longer in this group.',
    );
  }

  try {
    await conv.removeMembers(leaves);
  } catch (err) {
    log.error({
      event: 'groupConversation.remove_members_failed',
      conversationIdPrefix: conversationIdHex.slice(0, 8),
      leafCount: leaves.length,
    });
    throw new GroupCreateError(
      'add_members_failed',
      err instanceof Error ? err.message : 'Could not remove members from the group.',
    );
  }

  log.info({
    event: 'groupConversation.members_removed',
    conversationIdPrefix: conversationIdHex.slice(0, 8),
    removedAccounts: removedAccounts.size,
    removedLeaves: leaves.length,
  });

  return {
    conv,
    removedAccountIds: [...removedAccounts],
    removedLeafCount: leaves.length,
  };
}

async function claimAllPeerDevices(
  peerAccountIds: readonly string[],
  claimDevicesForAccount: ClaimDevicesForAccount,
  signal?: AbortSignal,
): Promise<ClaimedDevice[][]> {
  const results: ClaimedDevice[][] = new Array(peerAccountIds.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    for (;;) {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      const i = nextIndex;
      nextIndex += 1;
      if (i >= peerAccountIds.length) return;
      const accountId = peerAccountIds[i]!;
      const devices = await claimDevicesForAccount(accountId, signal);
      if (devices.length === 0) {
        throw new GroupCreateError(
          'peer_unavailable',
          'That person is not ready to receive invites — ask them to open Ping.',
          accountId,
        );
      }
      results[i] = [...devices];
    }
  }

  const workers = Array.from(
    { length: Math.min(GROUP_CLAIM_CONCURRENCY, peerAccountIds.length) },
    () => worker(),
  );
  await Promise.all(workers);
  return results;
}

async function inviteDevicesToConversation(params: {
  readonly conv: MlsConversationLike;
  readonly transport: WelcomeRecipientSink | null;
  readonly conversationIdHex: string;
  readonly devices: readonly ClaimedDevice[];
  readonly log: MlsLogger;
  readonly kv?: KVStore;
  readonly localAccountId?: string;
  readonly peerAccountIds?: readonly string[];
  /** When true (add-to-existing), do not rewrite the create-time invite KV keys. */
  readonly skipKv?: boolean;
}): Promise<void> {
  const {
    conv,
    transport,
    conversationIdHex,
    devices,
    log,
    skipKv = false,
  } = params;

  const recipientHexes = devices.map((d) => d.deviceIdHex);
  // CRITICAL: with no transport we have nowhere to send the Welcomes, yet
  // `addMembers` would STILL advance our local MLS epoch — leaving the group
  // desynced from the BE so every later send 404s / epoch-mismatches (a
  // silently "broken invite"). The 1:1 path guards this (mlsConversation.ts);
  // group-create must too. Bail BEFORE `addMembers` so the epoch is untouched
  // and the next attempt (once the socket is back) re-invites cleanly without
  // a half-created, undeliverable group.
  if (!transport) {
    log.warn({
      event: 'groupConversation.no_transport',
      conversationIdPrefix: conversationIdHex.slice(0, 8),
    });
    throw new GroupCreateError(
      'add_members_failed',
      'You appear to be offline — reconnect and try again to invite members.',
    );
  }
  transport.setWelcomeRecipients(conversationIdHex, recipientHexes);

  const entries: MlsKeyPackageEntry[] = devices.map((d) => ({
    deviceId: base64urlToBytes(d.deviceIdHex),
    keyPackage: d.keyPackageBytes,
  }));

  await conv.addMembers(entries);

  if (
    skipKv ||
    params.kv === undefined ||
    params.localAccountId === undefined ||
    params.peerAccountIds === undefined
  ) {
    return;
  }

  const cacheKey = groupConversationCacheKey(params.localAccountId, params.peerAccountIds);
  const invitedPayload = {
    kind: 'group',
    peerAccountIds: params.peerAccountIds,
    deviceCount: devices.length,
    at: Date.now(),
  };
  const now = Date.now();
  await params.kv.put({
    key: INVITED_KEY_PREFIX + cacheKey,
    value: invitedPayload,
    updatedAt: now,
  });
  await params.kv.put({
    key: INVITED_KEY_PREFIX + conversationIdHex,
    value: invitedPayload,
    updatedAt: now,
  });
}
